/**
 * Sibling JSON next to a block-model zip, e.g. CP.zip + CP.json
 * {
 *   "grade": "Au_gpt",
 *   "dx": -2.4, "dy": 0, "dz": -300,
 *   "minGrade": 0.3,
 *   "sx": "XINC", "sy": "YINC", "sz": 1
 * }
 * sx/sy/sz: string = CSV column, number = fixed metres on that axis.
 */
export function zipSidecarUrl(zipUrl) {
  if (!zipUrl) return null;
  try {
    const u = new URL(String(zipUrl), typeof window !== 'undefined' ? window.location.href : 'http://local/');
    u.pathname = u.pathname.replace(/\.zip$/i, '.json');
    return u.toString();
  } catch {
    return String(zipUrl).replace(/\.zip(\?|$)/i, '.json$1');
  }
}

function parseSizeAxis(value) {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }
  if (typeof value === 'string') {
    const t = value.trim();
    return t || undefined;
  }
  return undefined;
}

function readSizeAxis(source, axis) {
  if (!source || typeof source !== 'object') return undefined;
  const key = `s${axis}`;
  return parseSizeAxis(
    source[key] ?? source[axis] ?? source[`${axis}Size`] ?? source[`size${axis.toUpperCase()}`]
  );
}

export function parseBlockModelSidecar(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
  const meta = {};
  if (typeof data.grade === 'string' && data.grade.trim()) meta.grade = data.grade.trim();
  if (typeof data.colorBy === 'string' && data.colorBy.trim()) meta.colorBy = data.colorBy.trim();
  if (typeof data.displayName === 'string' && data.displayName.trim()) {
    meta.displayName = data.displayName.trim();
  } else if (typeof data.name === 'string' && data.name.trim()) {
    meta.displayName = data.name.trim();
  }
  ['dx', 'dy', 'dz'].forEach((k) => {
    const n = Number(data[k]);
    if (Number.isFinite(n)) meta[k] = n;
  });
  const min = data.minGrade ?? data.min;
  const max = data.maxGrade ?? data.max;
  if (min != null && min !== '' && Number.isFinite(Number(min))) meta.minGrade = Number(min);
  if (max != null && max !== '' && Number.isFinite(Number(max))) meta.maxGrade = Number(max);
  const sizeSrc = data.blockSize && typeof data.blockSize === 'object' && !Array.isArray(data.blockSize)
    ? data.blockSize
    : data;
  const sx = readSizeAxis(sizeSrc, 'x');
  const sy = readSizeAxis(sizeSrc, 'y');
  const sz = readSizeAxis(sizeSrc, 'z');
  if (sx !== undefined) meta.sx = sx;
  if (sy !== undefined) meta.sy = sy;
  if (sz !== undefined) meta.sz = sz;
  return meta;
}

export function mergeBlockModelMeta(preferred, fallback) {
  const out = { ...(fallback || {}) };
  Object.entries(preferred || {}).forEach(([k, v]) => {
    if (v == null || v === '') return;
    out[k] = v;
  });
  return out;
}

export async function fetchBlockModelSidecar(zipUrl) {
  const sidecarUrl = zipSidecarUrl(zipUrl);
  if (!sidecarUrl) return {};
  try {
    const res = await fetch(sidecarUrl, { cache: 'no-store' });
    if (!res.ok) return {};
    return parseBlockModelSidecar(await res.json());
  } catch {
    return {};
  }
}
